/**
 * Create or edit one focus block: goal, day, start/end (15-minute steps),
 * protection, note — plus the Outlook sync line with Retry, Restore for a
 * block removed in Outlook, and a confirmed Delete.
 *
 * Form state is local and seeded once; the page remounts the modal per
 * target (`key`), so a live refresh never overwrites what is being typed.
 * Times are Berlin wall-clock and leave as offset ISO via `lib/focusTime`.
 * What a block may still change follows the server: a past block only takes
 * a note, a running one only its end, protection or note.
 */
import { useState } from "react";
import { clsx } from "clsx";

import type { BlockPatchBody, BlockProtection, FocusBlock } from "../../api/focus";
import { blockTiming } from "../../lib/focus";
import { formatRelative } from "../../lib/formatRelative";
import {
  DAY_MINUTES,
  berlinDate,
  berlinToUtc,
  formatDayLabel,
  formatHm,
  hmFromMinutes,
  parseHm,
  toBerlinIso,
} from "../../lib/focusTime";
import { Modal } from "../Modal";

export type BlockEditorTarget =
  | { kind: "create"; date: string; startMin: number; endMin: number; goalId: string | null }
  | { kind: "edit"; block: FocusBlock };

export interface BlockDraft {
  goal_id: string;
  starts_at: string;
  ends_at: string;
  protection: BlockProtection;
  note: string | null;
}

interface GoalOption {
  id: string;
  title: string;
}

interface FormState {
  goalId: string;
  date: string;
  start: string;
  end: string;
  protection: BlockProtection;
  note: string;
}

function initialForm(target: BlockEditorTarget, goals: GoalOption[]): FormState {
  if (target.kind === "create") {
    return {
      goalId: target.goalId ?? goals[0]?.id ?? "",
      date: target.date,
      start: hmFromMinutes(target.startMin),
      end: hmFromMinutes(target.endMin),
      protection: "soft",
      note: "",
    };
  }
  const b = target.block;
  return {
    goalId: b.goal_id,
    date: berlinDate(Date.parse(b.starts_at)),
    start: formatHm(Date.parse(b.starts_at)),
    end: formatHm(Date.parse(b.ends_at)),
    protection: b.protection,
    note: b.note ?? "",
  };
}

/** Wall times → instants; "00:00" as an end means the next midnight. */
function resolveTimes(form: FormState): { starts: number; ends: number } | string {
  const startMin = parseHm(form.start);
  let endMin = parseHm(form.end);
  if (startMin === null || endMin === null) return "Enter start and end as HH:MM";
  if (endMin === 0) endMin = DAY_MINUTES;
  if (endMin <= startMin) return "A block ends after it starts";
  if (endMin - startMin < 5) return "A block lasts at least 5 minutes";
  return { starts: berlinToUtc(form.date, startMin), ends: berlinToUtc(form.date, endMin) };
}

export function BlockEditorModal({
  target,
  goals,
  days,
  nowMs,
  busy,
  onCreate,
  onSave,
  onDelete,
  onRetrySync,
  onRestore,
  onClose,
}: {
  target: BlockEditorTarget;
  /** Plannable goals; an edited block's own goal is added if it isn't one. */
  goals: GoalOption[];
  days: string[];
  nowMs: number;
  busy: boolean;
  onCreate: (draft: BlockDraft) => void;
  onSave: (block: FocusBlock, patch: BlockPatchBody) => void;
  onDelete: (block: FocusBlock) => void;
  onRetrySync: (block: FocusBlock) => void;
  onRestore: (block: FocusBlock) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<FormState>(() => initialForm(target, goals));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const block = target.kind === "edit" ? target.block : null;
  const timing = block ? blockTiming(block, nowMs) : "future";
  const removed = block ? block.status !== "planned" : false;

  const goalOptions =
    block && !goals.some((g) => g.id === block.goal_id)
      ? [...goals, { id: block.goal_id, title: `${block.goal_title ?? "Goal"} (not plannable)` }]
      : goals;
  const dayOptions = days.includes(form.date) ? days : [form.date, ...days];

  const lockSchedule = timing !== "future" || removed;
  const lockEnd = timing === "past" || removed;
  const lockProtection = timing === "past" || removed;

  const times = resolveTimes(form);
  const error = typeof times === "string" ? times : null;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function submit() {
    if (typeof times === "string") return;
    const note = form.note.trim() || null;
    if (!block) {
      if (!form.goalId) return;
      onCreate({
        goal_id: form.goalId,
        starts_at: toBerlinIso(times.starts),
        ends_at: toBerlinIso(times.ends),
        protection: form.protection,
        note,
      });
      return;
    }
    // Only send what this block may still change, so a seconds-precise start
    // never reads as a "move" on a running block.
    const patch: BlockPatchBody = {};
    if (!lockSchedule && form.goalId !== block.goal_id) patch.goal_id = form.goalId;
    if (!lockSchedule && times.starts !== Date.parse(block.starts_at)) {
      patch.starts_at = toBerlinIso(times.starts);
    }
    if (!lockEnd && times.ends !== Date.parse(block.ends_at)) patch.ends_at = toBerlinIso(times.ends);
    if (!lockProtection && form.protection !== block.protection) patch.protection = form.protection;
    if (note !== (block.note ?? null)) patch.note = note;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    onSave(block, { ...patch, expected_revision: block.revision });
  }

  const title = block ? (block.goal_title ?? "Focus block") : "New focus block";
  const eyebrow = block
    ? `${formatDayLabel(berlinDate(Date.parse(block.starts_at)))} · ${formatHm(Date.parse(block.starts_at))}–${formatHm(
        Date.parse(block.ends_at),
      )}`
    : "Plan time";

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      eyebrow={eyebrow}
      footer={
        <>
          {block ? (
            confirmDelete ? (
              <button
                type="button"
                className="btn btn-ghost mr-auto"
                disabled={busy}
                onClick={() => onDelete(block)}
              >
                Delete – removes it from Outlook too
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-ghost mr-auto"
                disabled={busy || timing === "past"}
                title={timing === "past" ? "A past block stays as history" : undefined}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </button>
            )
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          {removed && block ? (
            <button type="button" className="btn btn-main" disabled={busy} onClick={() => onRestore(block)}>
              Restore
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-main"
              disabled={busy || !!error || !form.goalId}
              onClick={submit}
            >
              {block ? "Save" : "Create block"}
            </button>
          )}
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {removed ? (
          <div className="focus-banner">
            Removed in Outlook. Restore puts it back in your calendar; Delete drops it here too.
          </div>
        ) : timing === "past" ? (
          <div className="focus-banner">This block is over – only its note can change.</div>
        ) : timing === "running" ? (
          <div className="focus-banner">Running now – you can still move its end, protection or note.</div>
        ) : null}

        <div>
          <label className="label" htmlFor="block-goal">
            Goal
          </label>
          <select
            id="block-goal"
            className="input"
            value={form.goalId}
            disabled={lockSchedule}
            onChange={(e) => set("goalId", e.target.value)}
          >
            {goalOptions.length === 0 ? <option value="">No plannable goal</option> : null}
            {goalOptions.map((g) => (
              <option key={g.id} value={g.id}>
                {g.title}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label" htmlFor="block-day">
              Day
            </label>
            <select
              id="block-day"
              className="input"
              value={form.date}
              disabled={lockSchedule}
              onChange={(e) => set("date", e.target.value)}
            >
              {dayOptions.map((d) => (
                <option key={d} value={d}>
                  {formatDayLabel(d)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="block-start">
              Start
            </label>
            <input
              id="block-start"
              className="input font-mono"
              type="time"
              step={900}
              value={form.start}
              disabled={lockSchedule}
              onChange={(e) => set("start", e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="block-end">
              End
            </label>
            <input
              id="block-end"
              className="input font-mono"
              type="time"
              step={900}
              value={form.end}
              disabled={lockEnd}
              onChange={(e) => set("end", e.target.value)}
            />
          </div>
        </div>
        {error ? <p className="text-xs text-yellow-dark">{error}</p> : null}

        <div>
          <span className="label">Protection</span>
          <div className="segmented" role="radiogroup" aria-label="Protection">
            {(
              [
                { value: "soft", label: "Soft", hint: "tentative in Outlook" },
                { value: "protected", label: "Protected", hint: "busy in Outlook" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={form.protection === opt.value}
                disabled={lockProtection}
                className={clsx("segmented-item", form.protection === opt.value && "is-active")}
                onClick={() => set("protection", opt.value)}
              >
                {opt.label}
                <span className="segmented-hint">{opt.hint}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label" htmlFor="block-note">
            Note
          </label>
          <input
            id="block-note"
            className="input"
            maxLength={500}
            placeholder="Optional – shows in the Outlook event"
            value={form.note}
            onChange={(e) => set("note", e.target.value)}
          />
        </div>

        {block ? <SyncLine block={block} busy={busy} onRetry={() => onRetrySync(block)} /> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function SyncLine({ block, busy, onRetry }: { block: FocusBlock; busy: boolean; onRetry: () => void }) {
  const { state, error, synced_at } = block.calendar;
  return (
    <div className={clsx("sync-line", state === "error" && "is-error")}>
      <span className="font-mono">outlook</span>
      {state === "synced" ? (
        <span>synced {synced_at ? formatRelative(synced_at) : ""}</span>
      ) : state === "pending" ? (
        <span>sync pending</span>
      ) : state === "error" ? (
        <>
          <span className="min-w-0 truncate" title={error ?? undefined}>
            error{error ? ` – ${error}` : ""}
          </span>
          <button type="button" className="btn btn-sm btn-ghost ml-auto" disabled={busy} onClick={onRetry}>
            Retry
          </button>
        </>
      ) : (
        <span>not connected – the block stays local</span>
      )}
    </div>
  );
}
